import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import GeoLayout from "./components/pages/geosafe/GeoLayout";
import SiLayout from "./components/pages/sportsIntegrity/SiLayout";
import KycLayout from "./components/pages/kyc/KycLayout";
import ContactLayout from "./components/pages/contact/ContactLayout";
import ScrollToTop from "./components/navigation/ScrollToTop";
import { getLogoutUrlWithRedirect } from "./constants/Constants";
import { cookiesCleanup } from "./utils/AuthUtils";
import { useUser } from "./context/UserContext";
import { PRODUCTS } from "./constants/permissions";

const Logout = () => {
  cookiesCleanup();
  window.location.replace(getLogoutUrlWithRedirect());
  return null; // render nothing
};

// Gate a product's guide behind its view permission. If the user lacks it, send
// them to the first product they can access (or a no-access page if none).
const ProtectedProduct = ({ productKey, children }) => {
  const { canAccess, accessibleProducts } = useUser();
  if (canAccess(productKey)) {
    return children;
  }
  if (accessibleProducts.length > 0) {
    return <Navigate to={PRODUCTS[accessibleProducts[0]].basePath} replace />;
  }
  return <Navigate to="/no-access" replace />;
};

// Landing route: send the user to the first product they can view.
const Landing = () => {
  const { accessibleProducts } = useUser();
  if (accessibleProducts.length > 0) {
    return <Navigate to={PRODUCTS[accessibleProducts[0]].basePath} replace />;
  }
  return <Navigate to="/no-access" replace />;
};

const NoAccess = () => (
  <div
    style={{
      padding: "3rem",
      textAlign: "center",
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <h2>No access</h2>
    <p
      style={{
        maxWidth: "90vw",
        width: "30rem",
        textAlign: "center",
        margin: "0 0 12rem 0",
        lineHeight: "1.5",
      }}
    >
      You don&apos;t have permission to view any integration guides. Please
      contact your administrator if you believe this is a mistake.
    </p>
  </div>
);

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route
          path="/introduction"
          element={
            <ProtectedProduct productKey="geo">
              <GeoLayout />
            </ProtectedProduct>
          }
        />
        <Route
          path="/introduction/:tab"
          element={
            <ProtectedProduct productKey="geo">
              <GeoLayout />
            </ProtectedProduct>
          }
        />
        <Route
          path="/sports-integrity"
          element={
            <ProtectedProduct productKey="si">
              <SiLayout />
            </ProtectedProduct>
          }
        />
        <Route
          path="/sports-integrity/:tab"
          element={
            <ProtectedProduct productKey="si">
              <SiLayout />
            </ProtectedProduct>
          }
        />
        <Route
          path="/kyc"
          element={
            <ProtectedProduct productKey="kyc">
              <KycLayout />
            </ProtectedProduct>
          }
        />
        <Route
          path="/kyc/:tab"
          element={
            <ProtectedProduct productKey="kyc">
              <KycLayout />
            </ProtectedProduct>
          }
        />
        <Route path="/contact" element={<ContactLayout />} />
        <Route path="/contact/:tab" element={<ContactLayout />} />
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="/logout" element={<Logout />} />
        {/* Any unmatched path falls back to the landing logic, which routes to
            the user's first accessible product (or /no-access if none). */}
        <Route path="*" element={<Landing />} />
      </Routes>
    </>
  );
}

export default AppRoutes;
